import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';

import calculatorButtonClick from '@hh.ru/analytics-js-events/build/career_platform/profession/calculator_button_click';
import calculatorSkillsToggleButtonClick from '@hh.ru/analytics-js-events/build/career_platform/profession/calculator_skills_toggle_button_click';
import { useSelector } from '@hh.ru/front-static-app';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';

import { uxfeedbackEvents } from 'src/modules/uxfeedback';
import postCalculate from 'src/pages/Profession/api/postCalculate';
import { EdProgramStatus } from 'src/types/EdProgram';
import { CalculatorSkill, CalculatorSkillId } from 'src/types/calculator';
import { ProfileSkillDto, ProfileSkillId } from 'src/types/profile';

import CalculatorContext from 'src/pages/Profession/components/SectionCalculator/Context/CalculatorContext';
import {
    DEBOUNCE_DELAY_MS,
    INITIAL_TABLO_DATA,
} from 'src/pages/Profession/components/SectionCalculator/Context/constants';
import {
    CalculatorContextState,
    CalculatorContextTabloData,
    LoadingState,
} from 'src/pages/Profession/components/SectionCalculator/Context/types';

interface CalculatorProviderProps {
    children: ReactNode;
}

const CalculatorProvider: FC<CalculatorProviderProps> = ({ children }) => {
    const {
        calculator: { skillsGroup },
        currentGrade,
        professionId,
        currentRegion,
    } = useSelectorNonNullable((state) => state.pageProfession);
    const profile = useSelector((state) => state.profile);

    const gradeId = currentGrade?.id || '';

    const [selectedSkills, setSelectedSkills] = useState<CalculatorSkill[]>([]);
    const [isLoginModalOpen, setLoginModalOpen] = useState(false);
    const [isFillSkillsModalOpen, setFillSkillsModalOpen] = useState(false);

    const isInit = useRef<boolean>(false);
    const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isSendAnalytics = useRef<boolean>(false);

    const skillsGrouped = useMemo(() => {
        const onlySkills = skillsGroup.map(({ skills }) => skills).reduce((acc, curr) => acc.concat(curr), []);
        const map = new Map<CalculatorSkillId, CalculatorSkill>();

        onlySkills.forEach((skill) => {
            map.set(skill.id, skill);
        });

        return {
            all: onlySkills,
            map,
        };
    }, [skillsGroup]);

    const selectedSkillsGrouped = useMemo(() => {
        const withCourse: CalculatorSkill[] = [];
        const withoutCourse: CalculatorSkill[] = [];
        const map = new Map<CalculatorSkillId, CalculatorSkill>();

        selectedSkills.forEach((skill) => {
            if (skill.course && skill.course.status !== EdProgramStatus.PROGRESS) {
                withCourse.push(skill);
            } else {
                withoutCourse.push(skill);
            }

            map.set(skill.id, skill);
        });

        return {
            withCourse,
            withoutCourse,
            map,
            all: selectedSkills,
            isSelectSkills: !!selectedSkills.length,
        };
    }, [selectedSkills]);

    const profileSkillsMap = useMemo(() => {
        const result = new Map<ProfileSkillId, ProfileSkillDto>();

        if (profile?.skills) {
            profile.skills.forEach((skill) => {
                result.set(skill.id, skill);
            });
        }

        return result;
    }, [profile]);

    const intersectionSkills = useMemo(
        () => [...profileSkillsMap.values()].filter(({ id }) => skillsGrouped.map.has(id)),
        [profileSkillsMap, skillsGrouped]
    );

    const isFilledByProfile = useMemo(() => {
        if (!profile?.skills.length) {
            return false;
        }

        return intersectionSkills.every(({ id }) => selectedSkillsGrouped.map.has(id));
    }, [selectedSkillsGrouped, intersectionSkills, profile]);

    const onSendAnalytics = () => {
        uxfeedbackEvents.professionPageCalculatorUsed();

        if (!isSendAnalytics.current) {
            isSendAnalytics.current = true;
            calculatorButtonClick({ professionId, levelId: gradeId });
        }
    };

    const handleToggleSkill = (skill: CalculatorSkill) => {
        onSendAnalytics();
        if (selectedSkillsGrouped.map.has(skill.id)) {
            setSelectedSkills((prev) => prev.filter((s) => s.id !== skill.id));
            return;
        }
        setSelectedSkills((prev) => [...prev, skill]);
    };

    const setSkillsByProfile = useCallback(() => {
        setSelectedSkills(skillsGrouped.all.filter((s) => profileSkillsMap.has(s.id)));
    }, [profileSkillsMap, skillsGrouped]);

    const clearSkills = useCallback(() => {
        setSelectedSkills([]);
    }, []);

    const handleFill = useCallback(() => {
        calculatorSkillsToggleButtonClick({ professionId, levelId: gradeId });
        if (!profile) {
            setLoginModalOpen(true);
            return;
        }

        if (profileSkillsMap.size === 0) {
            setFillSkillsModalOpen(true);
            return;
        }

        if (isFilledByProfile) {
            clearSkills();
            return;
        }
        setSkillsByProfile();
    }, [professionId, gradeId, clearSkills, isFilledByProfile, profile, profileSkillsMap, setSkillsByProfile]);

    const handleLoginClose = () => setLoginModalOpen(false);
    const handleFillSkillsClose = () => setFillSkillsModalOpen(false);

    //  LOAD LOGIC
    const [loadingState, setLoadingState] = useState(LoadingState.INITIAL);
    const [tabloData, setTabloData] = useState<CalculatorContextTabloData>(INITIAL_TABLO_DATA);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [error, setError] = useState<AxiosError>();
    const controller = useRef<AbortController | null>(null);

    const onFetch = useCallback(
        async (selectedIds: CalculatorSkillId[]) => {
            try {
                if (!selectedIds.length) {
                    setLoadingState(LoadingState.INITIAL);
                    setTabloData(INITIAL_TABLO_DATA);
                    return;
                }
                setLoadingState(LoadingState.LOADING);

                controller.current = new AbortController();

                const { salary, skillComparison, vacancies } = await postCalculate(
                    {
                        professionId,
                        skillIds: selectedIds,
                        gradeId,
                        areaId: currentRegion,
                    },
                    {
                        signal: controller.current?.signal,
                    }
                );

                setTabloData({
                    salary,
                    vacancies,
                    skillComparison,
                });
                setError(undefined);
                setLoadingState(LoadingState.LOADED);

                controller.current = null;
            } catch (err) {
                if (!axios.isCancel(err) && axios.isAxiosError(err)) {
                    setTabloData(INITIAL_TABLO_DATA);
                    setError(err);
                    setLoadingState(LoadingState.ERROR);
                }
            }
        },
        [professionId, gradeId, currentRegion]
    );

    const onDebounceFetch = useCallback(
        (selectedIds: CalculatorSkillId[]) => {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }

            // осознанно прерываем тут, а не в запросе
            // нужно чтобы отменять предыдущий запрос (если он есть), пока не запустили новый
            if (controller.current) {
                controller.current.abort();
                controller.current = null;
            }

            timeout.current = setTimeout(() => onFetch(selectedIds), DEBOUNCE_DELAY_MS);
        },
        [onFetch]
    );

    useEffect(() => {
        if (isInit.current) {
            const ids = selectedSkills.map(({ id }) => id);
            onDebounceFetch(ids);
        } else {
            isInit.current = true;
        }
    }, [onDebounceFetch, selectedSkills]);

    useEffect(() => {
        return () => {
            if (controller.current) {
                controller.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        isSendAnalytics.current = false;
    }, [professionId, currentGrade]);

    const shouldBeHidden = useMemo(
        () => !!profile && profileSkillsMap.size !== 0 && intersectionSkills.length === 0,
        [profile, profileSkillsMap, intersectionSkills]
    );

    useEffect(() => {
        !shouldBeHidden && intersectionSkills.length && setSkillsByProfile();
    }, [setSkillsByProfile, intersectionSkills, shouldBeHidden]);

    const value: CalculatorContextState = {
        professionId,
        gradeId,
        gradeName: currentGrade?.name || '',
        skills: skillsGrouped,
        skillsGroup,
        selectedSkills: selectedSkillsGrouped,
        profileSkillsMap,
        tablo: {
            data: tabloData,
            isInitial: loadingState === LoadingState.INITIAL,
            isLoading: loadingState === LoadingState.LOADING,
            isLoaded: loadingState === LoadingState.LOADED,
            isLoadError: loadingState === LoadingState.ERROR,
        },
        fillByProfile: {
            isFilled: isFilledByProfile,
            handleFill,
            isHidden: shouldBeHidden,
        },
        modalFillSkills: {
            isOpen: isFillSkillsModalOpen,
            onClose: handleFillSkillsClose,
        },
        modalLogin: {
            isOpen: isLoginModalOpen,
            onClose: handleLoginClose,
        },
        handleToggleSkill,
    };

    return <CalculatorContext.Provider value={value}>{children}</CalculatorContext.Provider>;
};

export default CalculatorProvider;
